import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import FaqV2 from "@/components/Home/v2/FaqV2";
import FeaturesGridV2 from "@/components/Home/v2/FeaturesGridV2";
import StoriesMarqueeV2 from "@/components/Home/v2/StoriesMarqueeV2";
import SectionHeader from "@/components/Home/v2/shared/SectionHeader";
import { homeV2Data } from "@/shared/data/home.v2.data";
import {
  BookADemoSourceEnum,
  CtaActionType,
  CtaType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { DEFAULT_CALENDLY_LINK } from "@/shared/constants/calendly.constants";
import Image from "next/image";
import Link from "next/link";

import GraphyVsComparisonSection from "./GraphyVsComparisonSection";
import { getCDNURL } from "@/shared/utils/commons";

/**
 * /page/graphy-vs-alternative — India only.
 *
 * Minimal header (GRAPHY logo + "Book A Demo"), no footer, 5 sections:
 *   1. Hero — Compare Graphy with Other Course Platforms + CTAs + trust badges
 *   2. Feature comparison table (GraphyVsComparisonSection)
 *   3. Features grid — "Features which makes Graphy a better choice"
 *   4. Stories — "Creators Who Sell More, Without Doing More"
 *   5. FAQ
 */

/* ── Shared CTA definitions ────────────────────────────────────────── */

const heroCtas: CtaType[] = [
  {
    type: ButtonTypeEnum.Secondary,
    size: ButtonSizeEnum.Regular,
    text: "Start Your Free Trial",
    actionType: CtaActionType.SignUp,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      eventProperties: { source: SignUpSourceEnum.hero },
    },
  },
  {
    type: ButtonTypeEnum.Primary,
    size: ButtonSizeEnum.Regular,
    text: "Book A Demo",
    actionType: CtaActionType.BookDemo,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
      eventProperties: { source: BookADemoSourceEnum.hero },
    },
  },
];

const bodyCtas: CtaType[] = [
  {
    type: ButtonTypeEnum.Secondary,
    size: ButtonSizeEnum.Regular,
    text: "Start Your Free Trial",
    actionType: CtaActionType.SignUp,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      eventProperties: { source: SignUpSourceEnum.body_end },
    },
  },
  {
    type: ButtonTypeEnum.Primary,
    size: ButtonSizeEnum.Regular,
    text: "Book A Demo",
    actionType: CtaActionType.BookDemo,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
      eventProperties: { source: BookADemoSourceEnum.bodyEnd },
    },
  },
];

const faqCtas: CtaType[] = [
  {
    type: ButtonTypeEnum.Primary,
    size: ButtonSizeEnum.Regular,
    text: "Start Your Free Trial",
    actionType: CtaActionType.SignUp,
    amplitudeEvent: {
      eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      eventProperties: { source: SignUpSourceEnum.body_end },
    },
  },
];

/* ── Header (shared with /page/classplus-alternative etc.) ────────── */

const AlternativeHeader = () => (
  <header className="sticky top-0 z-bar w-full border-b border-transparent bg-white/60 font-inter backdrop-blur-md">
    <div className="container mx-auto px-4 py-4 sm:px-6 md:px-8 lg:px-12">
      <div className="mx-auto flex max-w-6xl items-center justify-between">
        <Link href="/" aria-label="Graphy home">
          <Image
            src="/images/graphy_logo_black.svg"
            width={110}
            height={32}
            alt="Graphy"
            priority
            className="h-7 w-auto md:h-8"
          />
        </Link>
        <Link
          href={DEFAULT_CALENDLY_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full bg-brandPrimary px-5 py-2.5 font-inter text-[14px] font-medium text-white transition-opacity hover:opacity-90 md:text-[15px]"
        >
          Book a Demo
        </Link>
      </div>
    </div>
  </header>
);

/* ── Section 1: Hero ───────────────────────────────────────────────── */

const Stars = () => (
  <div className="flex items-center gap-0.5 text-[14px] text-[#F5A524]">
    <span>★</span>
    <span>★</span>
    <span>★</span>
    <span>★</span>
    <span className="relative inline-block">
      <span className="text-[#E5E7EB]">★</span>
      <span className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
        ★
      </span>
    </span>
  </div>
);

const ReviewBadges = () => (
  <div className="mt-6 flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-10 md:mt-8 md:gap-14">
    {[
      { src: "/images/categories/home-testimonials/g2-crowd.svg", alt: "G2 Crowd" },
      { src: "/images/categories/home-testimonials/capterra.svg", alt: "Capterra" },
      { src: "/images/categories/home-testimonials/trustpilot.svg", alt: "Trustpilot" },
    ].map((b, i) => (
      <div key={b.alt} className="flex items-center gap-6 sm:gap-10 md:gap-14">
        <div className="flex flex-col items-center gap-2">
          <Image src={b.src} alt={b.alt} width={140} height={40} />
          <Stars />
        </div>
        {i < 2 && <div className="hidden h-10 w-px bg-black/10 sm:block" />}
      </div>
    ))}
  </div>
);

const Hero = ({ ctas }: { ctas: CtaType[] }) => (
  <section
    className="relative w-full overflow-hidden"
    style={{
      background:
        "radial-gradient(120% 80% at 50% 100%, rgba(255, 210, 170, 0.28) 0%, rgba(255, 198, 217, 0.25) 35%, rgba(249, 216, 231, 0.18) 60%, rgba(255, 255, 255, 1) 100%)",
    }}
  >
    <div className="container mx-auto px-4 pb-20 pt-10 sm:px-6 md:px-8 md:pb-28 md:pt-14 lg:px-12 lg:pb-36 lg:pt-20">
      <SectionHeader
        titleMuted="Compare Graphy with"
        titleBright="Other Course Platforms"
        subtitle="Compare pricing, features, and platform capabilities across top course platforms — and make an informed decision."
        ctas={ctas}
        variant="light-magenta"
        align="center"
      />

      <p className="mt-10 text-center text-[15px] font-semibold text-[#4F46E5] md:mt-12 md:text-[17px]">
        Trusted by 200,000+ creators
      </p>

      <ReviewBadges />
    </div>
  </section>
);

/* ── Section 3: Features grid (shared component with HomeV2) ───────── */

const FeaturesGridSection = () => (
  <FeaturesGridV2
    {...homeV2Data.featuresGrid}
    title="Features which makes Graphy a better choice"
    subtitle="One platform. One login. One simple dashboard. No juggling 10 different tools or paying for 5 subscriptions."
  />
);

/* ── Section 4: Stories marquee (reuses product v2 scrolling component) ── */

const storyCards = [
  {
    title: "My Go-To Platform",
    quote:
      "Webveda, a 5 lakh+ student community is run by 1 full-time employee and 8 interns only. This is made possible only by Graphy. Right from conducting courses, assignments, live sessions to progress tracking, Graphy handles everything for us.",
    name: "Ankur Warikoo",
    role: "Coach",
    avatar:
      `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Ankur Warikoo.png`,
  },
  {
    title: "Everything In One Place",
    quote:
      "With Graphy, we've scaled our programs to reach aspiring professionals across countries — all from one platform. The ease of managing course content, payments, and learner communication has allowed us to focus more on mentorship and less on operations.",
    name: "Aarushi Bedi",
    role: "Founder | The Fluent Flyers",
    avatar:
      `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Aarushi Bedi.png`,
  },
  {
    title: "Reliable Partner",
    quote:
      "This is a human business. You don't choose a platform because it's cheap or fancy. You choose people you can depend on. And Graphy has been that partner for me.",
    name: "Atish Mathur",
    role: "Educator",
    avatar:
      `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Athish Mathur.png`,
  },
  {
    title: "Selling Made Simple",
    quote:
      "Graphy has made my life easier. I can finally focus on what I do best — teaching — while the platform takes care of everything else.",
    name: "Dr. Anand Mani",
    role: "Coach",
    avatar:
      `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Dr. Anand Mani.png`,
  },
  {
    title: "Scaled Globally",
    quote:
      "Multi-currency checkout and automated tax were huge. I stopped losing international buyers at checkout and my refund rate dropped to under 2%.",
    name: "Aman Sharma",
    role: "Finance Coach",
    avatar:
      `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Aman Sharma.png`,
  },
  {
    title: "Community Is Home",
    quote:
      "The white-label mobile app and built-in chat gave my members a home on their phones. Daily active users went up and churn dropped instantly.",
    name: "Dnyanadeep Academy",
    role: "Test-Prep Academy",
    avatar:
      `${getCDNURL()}graphy-web/V4/images/Testimonial Images/Dnyanadeep Academy.png`,
  },
];

const StoriesSection = ({ ctas }: { ctas: CtaType[] }) => (
  <StoriesMarqueeV2
    eyebrow="STORIES"
    titleMuted="Creators Who Sell More,"
    titleBright="Without Doing More"
    subtitle="Thousands of creators use Graphy to automate selling, deliver better experiences, and grow their course businesses globally."
    ctas={ctas}
    stories={storyCards}
  />
);

/* ── Section 5: FAQ ────────────────────────────────────────────────── */

const faqItems = [
  {
    question: "Can I cancel my Graphy subscription anytime?",
    answer: "Yes, you can cancel the subscription anytime.",
  },
  {
    question: "Does Graphy take a cut of my revenue?",
    answer:
      "No, Graphy does not take any cut of your revenue. You keep 100% of what you earn, minus standard payment gateway fees.",
  },
  {
    question: "Do I need a separate Zoom subscription for live sessions?",
    answer:
      "No, live sessions are built into Graphy. You don't need a separate Zoom subscription for up to 3 simultaneous live sessions.",
  },
  {
    question: "Do I need to pay extra for mobile apps?",
    answer:
      "No, branded mobile apps are included in your Graphy plan at no extra cost.",
  },
  {
    question: "What kind of support can I expect from Graphy?",
    answer:
      "Graphy offers dedicated customer support via email, chat, and scheduled calls with a success manager on eligible plans.",
  },
  {
    question: "Can I migrate my existing course to Graphy?",
    answer:
      "Yes, our onboarding team handles the migration end-to-end. You don't have to rebuild your content from scratch.",
  },
];

const FaqSection = ({ ctas }: { ctas: CtaType[] }) => (
  <section className="relative w-full bg-white">
    <FaqV2 title="Frequently Asked Questions" ctas={ctas} items={faqItems} />
  </section>
);

const signupCta: CtaType = {
  type: ButtonTypeEnum.Secondary,
  size: ButtonSizeEnum.Regular,
  text: "Start Your Free Trial",
  actionType: CtaActionType.DelayedHref,
  href: "https://graphy.com/signup/",
};

/* ── Page orchestrator ────────────────────────────────────────────── */

const GraphyVsAlternative = ({ locale }: { locale?: string }) => {
  const isIndia = locale === "in";
  const resolvedHeroCtas = isIndia
    ? heroCtas.map((c) => (c.actionType === CtaActionType.SignUp ? signupCta : c))
    : heroCtas;
  const resolvedBodyCtas = isIndia
    ? bodyCtas.map((c) => (c.actionType === CtaActionType.SignUp ? signupCta : c))
    : bodyCtas;
  const resolvedFaqCtas = isIndia ? [] : faqCtas;

  const resolvedCtaBanner = isIndia
    ? {
        ...homeV2Data.ctaBanner,
        ctas: homeV2Data.ctaBanner.ctas.map((c) =>
          c.actionType === CtaActionType.SignUp ? signupCta : c
        ),
      }
    : homeV2Data.ctaBanner;

  return (
    <div className="home-v2-scope relative">
      <AlternativeHeader />
      <main className="relative bg-white">
        <Hero ctas={resolvedHeroCtas} />
        <GraphyVsComparisonSection />
        <FeaturesGridSection />
        <StoriesSection ctas={resolvedBodyCtas} />
        <FaqSection ctas={resolvedFaqCtas} />
        <CtaBannerV2 {...resolvedCtaBanner} />
      </main>
    </div>
  );
};

export default GraphyVsAlternative;
